import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import useRefreshLocationData, {
  RefreshType,
} from "../../../containers/FacilityDetailsPage/hooks/useRefreshLocationData";
import { MediaItemCategory } from "../../../models/MediaItemCategory";
import DataSvc from "../../../services/dataSvc";
import { cropImage } from "../../../utils/imageUtils";
import DropdownSelect from "../../FormElement/DropdownSelect";
import InputTextArea from "../../FormElement/InputTextarea";
import { MediaUpload } from "../../FormElement/MediaUpload";
import { ModalPleaseWait } from "../../ModalPleaseWait";

import "./styles.scss";

export interface IUploadMediaDefaults {
  category?: MediaItemCategory;
}

export interface IModalUploadMediaProps {
  locationId: string;
  onClose: () => void;
  onSuccess?: () => void;
  defaults?: IUploadMediaDefaults;
}

export const ModalUploadMedia: React.FunctionComponent<
  IModalUploadMediaProps
> = ({ locationId, onClose, onSuccess, defaults }) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`modalUploadMedia.${key}`);

  const [files, setFiles] = useState<File[]>([]);
  const [filesError, setFilesError] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState<string>("");
  const [category, setCategory] = useState<MediaItemCategory>(
    defaults?.category ?? MediaItemCategory.CATEGORY_UNSPECIFIED
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const refresh = useRefreshLocationData(locationId);

  const canSubmit = files.length > 0 && !filesError;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    // Cover images will be rejected by the third party API
    // if they are not 16:9, so we crop them before uploading.
    const mediaFile =
      category === MediaItemCategory.COVER
        ? await cropImage(files[0])
        : files[0];

    const formData = new FormData();
    formData.append("media", mediaFile);
    formData.append("description", description);
    formData.append("category", String(category));

    setIsLoading(true);
    try {
      await DataSvc.uploadMedia(locationId, formData);
      toast.success(t("upload_success"));
      refresh(RefreshType.MEDIA);
      onClose();
      if (onSuccess) onSuccess();
    } catch {
      // Axios handles displaying the error automatically.
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal modal-default modal-upload-media">
      <div className="modal-mains">
        <button className="btn-close" onClick={onClose}></button>
        <div className="modal-mains__body">
          <h2>{t("title")}</h2>
          <div className="upload-section">
            <MediaUpload
              maxFiles={1}
              maxSizeMiB={5}
              minWidth={400}
              minHeight={300}
              files={files}
              changeFiles={setFiles}
              error={filesError}
              changeError={setFilesError}
            />
          </div>
          <div className="category-section">
            <DropdownSelect
              fieldLabel={t("category_label")}
              value={MediaItemCategory[category]}
              items={Object.keys(MediaItemCategory).filter((k) =>
                isNaN(Number(k))
              )}
              isTranslation={true}
              translationKey="modalUploadMedia.category"
              onChange={(key) =>
                setCategory(
                  MediaItemCategory[key as keyof typeof MediaItemCategory]
                )
              }
            />
          </div>
          <div className="description-section">
            <label className="field-label">{t("description_label")}</label>
            <InputTextArea
              value={description}
              onChange={setDescription}
              placeholder={t("description_placeholder")}
            />
          </div>
          <div className="bottom-btns">
            <button className="btn btn-border" onClick={onClose}>
              {t("cancel")}
            </button>
            <button
              className="btn btn-blue"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {t("upload")}
            </button>
          </div>
        </div>
      </div>
      {isLoading && <ModalPleaseWait description={t("please_wait")} />}
    </div>
  );
};
